/**
 * Main JS Generator — generates the client-side JavaScript for exported sites
 */

export function generateMainJS(settings: any, components: any): string {
  const hasDarkToggle = settings.darkModeToggle;

  return `/* ============================================
   MAIN.JS
   Generated by Studio v5.0.0
   ============================================ */

(function() {
  'use strict';

  // ============================================
  // MOBILE MENU
  // ============================================

  const menuToggle = document.getElementById('menu-toggle');
  const mobileNav = document.getElementById('mobile-nav');
  const body = document.body;

  if (menuToggle && mobileNav) {
    // QA: B-5 — Focus-Trap helper for mobile nav
    var navFocusTrapHandler = null;

    function enableNavFocusTrap() {
      var focusable = mobileNav.querySelectorAll('a, button');
      if (focusable.length) focusable[0].focus();
      navFocusTrapHandler = function(e) {
        if (e.key !== 'Tab') return;
        var els = mobileNav.querySelectorAll('a, button');
        if (!els.length) return;
        var first = els[0], last = els[els.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      };
      document.addEventListener('keydown', navFocusTrapHandler);
    }

    function disableNavFocusTrap() {
      if (navFocusTrapHandler) {
        document.removeEventListener('keydown', navFocusTrapHandler);
        navFocusTrapHandler = null;
      }
    }

    function closeNav() {
      menuToggle.classList.remove('open');
      mobileNav.classList.remove('open');
      menuToggle.setAttribute('aria-expanded', 'false');
      mobileNav.setAttribute('aria-hidden', 'true');
      body.style.overflow = '';
      disableNavFocusTrap(); // QA: B-5
      menuToggle.focus(); // QA: B-5 — restore focus to toggle
    }

    menuToggle.addEventListener('click', function() {
      const isOpen = menuToggle.classList.toggle('open');
      mobileNav.classList.toggle('open');
      menuToggle.setAttribute('aria-expanded', isOpen);
      mobileNav.setAttribute('aria-hidden', !isOpen);

      // Prevent body scroll when menu is open
      body.style.overflow = isOpen ? 'hidden' : '';

      // QA: B-5 — Focus-Trap
      if (isOpen) {
        enableNavFocusTrap();
      } else {
        disableNavFocusTrap();
        menuToggle.focus();
      }
    });

    // Close menu on link click
    mobileNav.querySelectorAll('a').forEach(function(link) {
      link.addEventListener('click', function() {
        closeNav();
      });
    });

    // Close menu on Escape
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && mobileNav.classList.contains('open')) {
        closeNav(); // QA: B-5
      }
    });
  }

  // ============================================
  // SMOOTH SCROLL
  // ============================================

  document.querySelectorAll('a[href^="#"]').forEach(function(anchor) {
    anchor.addEventListener('click', function(e) {
      const targetId = this.getAttribute('href');
      if (targetId === '#') return;

      const target = document.querySelector(targetId);
      if (target) {
        e.preventDefault();
        const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
        const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    });
  });

  // ============================================
  // PARTIALLY PERSISTENT HEADER (Best Practice 2025)
  // ============================================

  const header = document.querySelector('.header');

  if (header) {
    let lastScrollY = 0;
    let ticking = false;

    function updateHeader() {
      const currentScrollY = window.pageYOffset;

      // Add shadow when scrolled
      if (currentScrollY > 50) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }

      // At top - always show
      if (currentScrollY < 56) {
        header.classList.remove('header--hidden');
        header.classList.add('header--visible');
      }
      // Scrolling down - hide
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        header.classList.add('header--hidden');
        header.classList.remove('header--visible');
      }
      // Scrolling up - show
      else if (currentScrollY < lastScrollY) {
        header.classList.remove('header--hidden');
        header.classList.add('header--visible');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }

    // Respect reduced motion
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

${
  hasDarkToggle
    ? `
  // ============================================
  // DARK MODE TOGGLE
  // ============================================

  const darkToggle = document.getElementById('dark-toggle');
  const html = document.documentElement;

  // Check saved preference or system preference
  function getPreferredTheme() {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  // Apply theme
  function applyTheme(theme) {
    if (theme === 'dark') {
      html.classList.add('dark');
      body.classList.add('dark');
    } else {
      html.classList.remove('dark');
      body.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }

  // Initialize
  applyTheme(getPreferredTheme());

  // Toggle handler
  if (darkToggle) {
    darkToggle.addEventListener('click', function() {
      const current = html.classList.contains('dark') ? 'dark' : 'light';
      applyTheme(current === 'dark' ? 'light' : 'dark');
    });
  }

  // Listen for system preference changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', function(e) {
    if (!localStorage.getItem('theme')) {
      applyTheme(e.matches ? 'dark' : 'light');
    }
  });
`
    : ''
}

  // ============================================
  // INTERSECTION OBSERVER (Fade-in animations)
  // ============================================

  if ('IntersectionObserver' in window) {
    const fadeElements = document.querySelectorAll('.service-card, .testimonial, .team-member, .benefit');

    const fadeObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          fadeObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    fadeElements.forEach(function(el) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      fadeObserver.observe(el);
    });
  }

  // ============================================
  // FOCUS VISIBLE POLYFILL (for older browsers)
  // ============================================

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Tab') {
      document.body.classList.add('keyboard-nav');
    }
  });

  document.addEventListener('mousedown', function() {
    document.body.classList.remove('keyboard-nav');
  });

  // ============================================
  // QA: Wave 5 — SCROLL-REVEAL
  // ============================================
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    var revealSections = document.querySelectorAll('.services,.benefits,.team,.testimonials,.faq,.cta,.pricing-grid,.method-steps,.timeline,.values-grid,.tools-grid,.page-cta,.content-section,.about-hero');
    if (revealSections.length && 'IntersectionObserver' in window) {
      var revealObs = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });
      revealSections.forEach(function(s) { revealObs.observe(s); });
    }
  }

})();
`;
}
